//@flow
import React from 'react';
import {
  makeStyles,
  Card,
  CardContent,
  CardMedia,
  Typography,
  IconButton,
  Chip,
  Tooltip,
} from '@material-ui/core';
import {Edit, Delete, Star, StarBorder} from '@material-ui/icons';
import {Link} from 'react-router-dom';
import {colors} from '../../../../../assets/styles/colors';
import {getImageUrl} from '../../../../../utils/image_provider';
import {getStringDate} from '../../../../../utils/date';
import IosSwitch from '../../../../../elements/IosSwitch/IosSwitch';

const useStyles = makeStyles(theme => ({
  card: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    borderRadius: '0.75rem',
    boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.05)',
    transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
    '&:hover': {
      transform: 'scale(1.02)',
      boxShadow: '0px 4px 1rem rgba(0, 0, 0, 0.05)',
    },
    position: 'relative',
    overflow: 'visible',
  },
  media: {
    height: 160,
    backgroundColor: colors.grey100,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    borderRadius: '0.75rem 0.75rem 0 0',
  },
  content: {
    flexGrow: 1,
    padding: '.5rem',
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',

    [theme.breakpoints.up('md')]: {
      padding: '.75rem',
    },
  },
  title: {
    fontWeight: 600,
    fontSize: '.95rem',
    lineHeight: 1.3,
    color: colors.grey900,
    marginBottom: '0.25rem',
    display: '-webkit-box',
    '-webkit-line-clamp': 2,
    '-webkit-box-orient': 'vertical',
    overflow: 'hidden',

    [theme.breakpoints.up('lg')]: {
      fontSize: '1.1rem',
    },
  },
  meta: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    color: colors.grey600,
    fontSize: '0.85rem',
    marginBottom: '0.5rem',
    gap: '0.125rem',
  },
  metaDate: {
    fontWeight: 600,
    fontSize: '.75rem',
  },
  metaAuthor: {
    fontWeight: 400,
    color: colors.grey700,
    fontSize: '.875rem',
  },
  categories: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '0.25rem',
    marginBottom: '0.5rem',
  },
  categoryChip: {
    height: '24px',
    fontSize: '0.75rem',
    backgroundColor: colors.grey100,
    color: colors.grey700,
  },
  actions: {
    borderTop: `1px solid ${colors.grey200}`,
    padding: '0.75rem .75rem',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '0.5rem',

    [theme.breakpoints.up('md')]: {
      padding: '0.75rem .75rem',
    },
  },
  actionButtons: {
    display: 'flex',
    gap: '0.25rem',
  },
  switches: {
    display: 'flex',
    gap: '1rem',
    alignItems: 'center',
  },
  switchLabel: {
    fontSize: '0.75rem',
    color: colors.grey600,
    marginBottom: '2px',
    display: 'block',
  },
  featuredButton: {
    position: 'absolute',
    top: '0.5rem',
    right: '0.5rem',
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
    '&:hover': {
      backgroundColor: 'white',
    },
  },
}));

export default function BlogCard({
  post,
  hasBlogFeature,
  onDelete,
  onTogglePublish,
  onToggleFeatured,
  showTierRestrictionModal,
}: any) {
  const classes = useStyles();
  const imageUrl = getImageUrl(post.mainImageId);

  return (
    <Card className={classes.card}>
      <div style={{position: 'relative'}}>
        <CardMedia
          className={classes.media}
          image={imageUrl}
          title={post.title}
        />
        <Tooltip title={post.featured ? 'Quitar de destacados' : 'Destacar'}>
          <IconButton
            className={classes.featuredButton}
            size="small"
            onClick={() => onToggleFeatured(post.id, !post.featured)}
          >
            {post.featured ? (
              <Star style={{color: colors.warning}} />
            ) : (
              <StarBorder style={{color: colors.grey600}} />
            )}
          </IconButton>
        </Tooltip>
      </div>

      <CardContent className={classes.content}>
        <div className={classes.meta}>
          <span className={classes.metaDate}>
            {getStringDate(post.blogUpdatedAt)}
          </span>
          <strong className={classes.metaAuthor}>{post.author}</strong>
        </div>

        <Typography className={classes.title} variant="h6">
          {post.title}
        </Typography>

        <div className={classes.categories}>
          {post.categories?.slice(0, 3).map(cat => (
            <Chip
              key={cat.id}
              label={cat.name}
              className={classes.categoryChip}
              size="small"
            />
          ))}
          {post.categories?.length > 3 && (
            <Chip
              label={`+${post.categories.length - 3}`}
              className={classes.categoryChip}
              size="small"
            />
          )}
        </div>
      </CardContent>

      <div className={classes.actions}>
        <div className={classes.switches}>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            <span className={classes.switchLabel}>Publicado</span>
            <IosSwitch
              checked={post.enabled}
              onChange={() => onTogglePublish(post.id, !post.enabled)}
            />
          </div>
        </div>

        <div className={classes.actionButtons}>
          <Tooltip title="Editar">
            <IconButton
              component={hasBlogFeature ? Link : 'button'}
              to={`/my-store/blog/${post.id}`}
              onClick={!hasBlogFeature ? showTierRestrictionModal : undefined}
              size="small"
              style={{color: colors.primary}}
            >
              <Edit fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Eliminar">
            <IconButton
              onClick={() => onDelete(post.id)}
              size="small"
              style={{color: colors.error}}
            >
              <Delete fontSize="small" />
            </IconButton>
          </Tooltip>
        </div>
      </div>
    </Card>
  );
}
